import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DataUsageOutlinedIcon from '@mui/icons-material/DataUsageOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SettingsInputAntennaOutlinedIcon from '@mui/icons-material/SettingsInputAntennaOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { CardIcon, type CardIconTone } from './CardIcon';
import type { CardHue } from './hues';
import type { ILLUSTRATED_ACTION_CATEGORIES } from './actionCategories';

/**
 * One icon per CONCEPT, across every screen that uses one.
 *
 * The alternative — each page choosing an icon for each card as it is written —
 * produces a product where "Timeline" is a clock here and an arrow there, and
 * two cards showing the same thing look unrelated. That is worse than no icons,
 * because a reader learns to ignore them.
 *
 * So this is a closed vocabulary keyed by what a card IS, not by its title:
 * `Risk assessment` on an incident and `Why it matters` on a recommendation are
 * one concept and get one icon.
 *
 * **A card with no concept here gets NO icon.** Guessing one for every heading
 * is how the vocabulary stops meaning anything, and a card is perfectly
 * readable without one — the icon is an aid to scanning, never the label.
 *
 * The tone is a CATEGORY, not a severity: `risk` means "this card is about the
 * thing that went wrong". Severity is the risk badge's job and it carries a
 * word, because colour never carries meaning alone in this product.
 */
export type CardConcept =
  /** What went wrong, and the measurements of it. */
  | 'event'
  /** A score, a band, the arithmetic behind either. */
  | 'risk'
  /** A rule, a policy, a condition that was tested. */
  | 'rule'
  /** Advice the platform produced. */
  | 'advice'
  /** Somewhere on a map. */
  | 'location'
  /** A person, or a person and their vehicle. */
  | 'people'
  /** A vehicle in its own right. */
  | 'vehicle'
  /** A journey. */
  | 'trip'
  /** What happened over time, in order. */
  | 'timeline'
  /** Identifiers, versions, payloads — the record rather than the reading. */
  | 'record'
  /** A list of related things. */
  | 'list'
  /** Something a person wrote. */
  | 'note'
  /** A judgement recorded after the fact. */
  | 'outcome'
  /** Wording a model produced. */
  | 'model'
  /** A distribution, a ranking, a comparison across the fleet. */
  | 'chart'
  /** A reading off the engine bus, or the health of one. */
  | 'diagnostics'
  /** How something is configured. */
  | 'settings'
  /** A credential, a key, or who may hold one. */
  | 'security'
  /** A connection to something outside OIP. */
  | 'integration'
  /** How the product looks and who it says it is. */
  | 'brand'
  /** Something that runs, or is sent, on a timetable. */
  | 'schedule'
  /** Something the platform sends to a person. */
  | 'message'
  /** Consumption against an allowance. */
  | 'usage'
  /** Organisations inside organisations. */
  | 'hierarchy';

/**
 * Each concept's glyph, and the hue that tells its section from its neighbours.
 *
 * `hue` is absent on exactly two: `event` keeps the risk colour because that
 * card IS about the thing that went wrong, and `outcome` keeps green because it
 * is about a judgement recorded afterwards. Both are deliberate uses of a
 * meaning the product already has. Everything else takes a hue that means
 * nothing — sequence, not severity.
 */
const VOCABULARY: Record<
  CardConcept,
  { icon: JSX.Element; tone: CardIconTone; hue?: CardHue }
> = {
  event: { icon: <ReportProblemOutlinedIcon />, tone: 'risk' },
  risk: { icon: <InsightsOutlinedIcon />, tone: 'info', hue: 'indigo' },
  rule: { icon: <RuleOutlinedIcon />, tone: 'info', hue: 'violet' },
  advice: { icon: <LightbulbOutlinedIcon />, tone: 'info', hue: 'blue' },
  location: { icon: <PlaceOutlinedIcon />, tone: 'info', hue: 'cyan' },
  people: { icon: <PersonOutlineIcon />, tone: 'info', hue: 'indigo' },
  vehicle: { icon: <LocalShippingOutlinedIcon />, tone: 'info', hue: 'steel' },
  trip: { icon: <RouteOutlinedIcon />, tone: 'info', hue: 'cyan' },
  timeline: { icon: <HistoryOutlinedIcon />, tone: 'neutral', hue: 'slate' },
  record: { icon: <DescriptionOutlinedIcon />, tone: 'neutral', hue: 'slate' },
  list: { icon: <ListAltOutlinedIcon />, tone: 'neutral', hue: 'slate' },
  note: { icon: <ChatOutlinedIcon />, tone: 'neutral', hue: 'plum' },
  outcome: { icon: <TaskAltOutlinedIcon />, tone: 'good' },
  model: { icon: <AutoAwesomeOutlinedIcon />, tone: 'info', hue: 'violet' },
  chart: { icon: <BarChartOutlinedIcon />, tone: 'info', hue: 'blue' },
  diagnostics: { icon: <MonitorHeartOutlinedIcon />, tone: 'info', hue: 'steel' },
  settings: { icon: <TuneOutlinedIcon />, tone: 'neutral', hue: 'slate' },
  security: { icon: <LockOutlinedIcon />, tone: 'neutral', hue: 'plum' },
  integration: { icon: <CableOutlinedIcon />, tone: 'info', hue: 'cyan' },
  brand: { icon: <PaletteOutlinedIcon />, tone: 'info', hue: 'violet' },
  schedule: { icon: <ScheduleOutlinedIcon />, tone: 'neutral', hue: 'indigo' },
  message: { icon: <NotificationsNoneOutlinedIcon />, tone: 'info', hue: 'blue' },
  usage: { icon: <DataUsageOutlinedIcon />, tone: 'info', hue: 'plum' },
  hierarchy: { icon: <AccountTreeOutlinedIcon />, tone: 'info', hue: 'indigo' },
};

/** The tile for a concept, ready for `DetailCard`'s `icon` slot. */
export function cardIcon(concept: CardConcept): JSX.Element {
  const { icon, tone, hue } = VOCABULARY[concept];
  return (
    <CardIcon tone={tone} hue={hue}>
      {icon}
    </CardIcon>
  );
}

/** The hue a concept uses, for a caller tinting more than the tile. */
export function conceptHue(concept: CardConcept): CardHue | undefined {
  return VOCABULARY[concept].hue;
}

/**
 * An icon for a recommendation's action category.
 *
 * A SEPARATE vocabulary from the card concepts above, and open where that one is
 * closed. `CardConcept` is a union, so a card asking for one that does not exist
 * is a compile error. `actionCategory` is `z.string().min(1)` in the contract and
 * nothing downstream switches on it — the policy editor offers
 * `SUGGESTED_ACTION_CATEGORIES` as suggestions rather than a constraint, because
 * a closed dropdown was tried and missed four categories the shipped policies
 * actually use, rendering them as an empty select: a rule the author could not
 * read, on the screen built to make rules readable.
 *
 * So this returns NULL for anything it does not recognise. A category somebody
 * invented in the editor keeps its title-cased name and simply has no glyph,
 * which is the same rule the card concepts follow — and the alternative, a
 * fallback icon, would quietly say "this is a known kind of action" about one
 * nobody has catalogued.
 *
 * **All one tone, deliberately.** The design this follows draws Escalate in red
 * and Coach driver in blue, which reads as severity — and severity is the risk
 * badge's job. A LOW-risk recommendation can legitimately say escalate, and a
 * red glyph beside it would contradict the score next to it.
 */
const ACTION_ICONS: Readonly<Record<(typeof ILLUSTRATED_ACTION_CATEGORIES)[number], JSX.Element>> = {
  REVIEW: <FactCheckOutlinedIcon />,
  REVIEW_BEHAVIOUR: <PersonSearchOutlinedIcon />,
  COACH_DRIVER: <SchoolOutlinedIcon />,
  CONTACT_DRIVER: <PhoneOutlinedIcon />,
  ESCALATE: <PriorityHighOutlinedIcon />,
  INVESTIGATE: <SearchOutlinedIcon />,
  INSPECT_VEHICLE: <BuildOutlinedIcon />,
  INSPECT_DEVICE: <SettingsInputAntennaOutlinedIcon />,
  SCHEDULE_MAINTENANCE: <EventAvailableOutlinedIcon />,
  CHECK_AUTHORISATION: <VerifiedUserOutlinedIcon />,
  VERIFY_ASSIGNMENT: <AssignmentIndOutlinedIcon />,
  /*
   * A note being written, not a tick or a cross. "Recorded, not chased" is an
   * act of writing something down — a tick would read as "done" and a cross as
   * "dismissed", and it is neither: nobody decided anything, the platform is
   * saying this is worth knowing and not worth chasing.
   */
  NO_ACTION_NEEDED: <EditNoteOutlinedIcon />,
};

/** The glyph for an action category, or null when it is not one we know. */
export function actionIcon(category: string): JSX.Element | null {
  // Indexed with a plain string on purpose: the CALLER has free text from the
  // contract, and narrowing the parameter to the known keys would move the
  // problem to every call site rather than solving it here.
  return (ACTION_ICONS as Record<string, JSX.Element | undefined>)[category] ?? null;
}
